import { Component, Type, Property } from '@wonderlandengine/api';
import {
    init,
    NavMeshQuery,
    Crowd,
} from 'recast-navigation';
import { generateSoloNavMesh } from 'recast-navigation/generators';

/* ─────────────────────────────────────────────────────────────────────────────
 *  Global singletons – imported by bot.js (unchanged API)
 * ───────────────────────────────────────────────────────────────────────────── */
export let globalNavMesh = null;
export let globalQuery   = null;

/* ─────────────────────────────────────────────────────────────────────────────
 *  NavQueue  –  time-slicing for pathfinding requests (unchanged API)
 *  bot.js enqueues closures here; update() drains up to MAX_CALCULATIONS_PER_FRAME
 *  per frame so we never spike the main thread.
 * ───────────────────────────────────────────────────────────────────────────── */
export const navQueue = {
    queue: [],
    MAX_CALCULATIONS_PER_FRAME: 2,
    enqueue(task) {
        this.queue.push(task);
    },
    update() {
        let count = 0;
        while (this.queue.length > 0 && count < this.MAX_CALCULATIONS_PER_FRAME) {
            const task = this.queue.shift();
            try { task(); } catch (e) { console.error('[NavQueue] Error:', e); }
            count++;
        }
    },
};

/* ─────────────────────────────────────────────────────────────────────────────
 *  NavManager  –  Wonderland Engine Component
 *
 *  Drop this on a scene object alongside (or after) VoxelTreadmill.
 *  It hooks into VoxelTreadmill's WorldSampler to walk the loaded chunks and
 *  build a Recast navmesh from the actual voxel surface heights instead of a
 *  pre-baked .bin file.
 *
 *  Component properties
 *  ────────────────────
 *  voxelTreadmill   WL object reference – the object that carries VoxelTreadmill
 *  cellSize         Recast cs  (horizontal voxel size, default 0.3)
 *  cellHeight       Recast ch  (vertical voxel size, default 0.2)
 *  agentHeight      walkable agent height in world units (default 2)
 *  agentRadius      walkable agent radius            (default 0.35)
 *  agentMaxClimb    max step a bot can climb          (default 1.1)
 *  agentMaxSlope    max navigable slope in degrees    (default 45)
 *  rebuildOnMine    rebuild after voxel destruction?  (default false – expensive)
 *  buildRadiusChunks  how many chunks around player to bake (default 3, match renderDistance)
 * ───────────────────────────────────────────────────────────────────────────── */
export class NavManager extends Component {
    static TypeName = 'nav-manager';

    static Properties = {
        voxelTreadmill:     Property.object(),
        cellSize:           Property.float(0.3),
        cellHeight:         Property.float(0.2),
        agentHeight:        Property.float(2.0),
        agentRadius:        Property.float(0.35),
        agentMaxClimb:      Property.float(1.1),
        agentMaxSlope:      Property.float(45.0),
        rebuildOnMine:      Property.bool(false),
        buildRadiusChunks:  Property.int(3),
    };

    /* ── lifecycle ──────────────────────────────────────────────────────────── */

    async init() {
        await init();    // initialises the Recast WASM module once
        console.log('[NavManager] Recast initialised');
    }

    start() {
        this._treadmill = this.voxelTreadmill
            ? this.voxelTreadmill.getComponent('voxel-treadmill')
            : null;

        if (!this._treadmill) {
            console.warn('[NavManager] voxelTreadmill property not set or component missing. ' +
                         'Assign the VoxelTreadmill object in the editor.');
        }

        this._built      = false;
        this._buildTimer = 1.5;  // wait 1.5 s after start so the first chunks finish generating

        // Allow other systems to request a rebuild at runtime
        // e.g.  window.NavManager.rebuild()
        try { window.NavManager = this; } catch (_) {}
    }

    update(dt) {
        navQueue.update();

        // Build once after the initial generation burst settles
        if (!this._built && this._treadmill) {
            this._buildTimer -= dt;
            if (this._buildTimer <= 0) {
                this._built = true;
                this.rebuild();
            }
        }
    }

    /* ── public API ─────────────────────────────────────────────────────────── */

    /**
     * (Re-)build the navmesh from the current VoxelTreadmill state.
     * Safe to call at runtime after terrain edits if rebuildOnMine is true.
     */
    rebuild() {
        if (!this._treadmill) {
            console.warn('[NavManager] rebuild() called but no VoxelTreadmill found.');
            return;
        }

        // Ensure treadmill has actually loaded chunks before trying to extract geometry
        if (this._treadmill._chunks.size === 0) {
            this._built = false; // reset to try again next frame
            return;
        }

        console.log('[NavManager] Building navmesh from voxel data…');

        try {
            const { positions, indices } = this._extractSurfaceGeometry();

            if (positions.length === 0) {
                console.warn('[NavManager] No geometry extracted – skipping navmesh build.');
                return;
            }

            /* ── Recast config ────────────────────────────────────────────── */
            const config = {
                cs:             this.cellSize,
                ch:             this.cellHeight,
                walkableHeight: Math.ceil(this.agentHeight   / this.cellHeight),
                walkableRadius: Math.ceil(this.agentRadius   / this.cellSize),
                walkableClimb:  Math.ceil(this.agentMaxClimb / this.cellHeight),
                walkableSlopeAngle: this.agentMaxSlope,
                minRegionArea:  8,
                mergeRegionArea: 20,
                maxEdgeLen:     12,
                maxSimplificationError: 1.3,
                maxVertsPerPoly: 6,
                detailSampleDist: 6,
                detailSampleMaxError: 1,
            };

            const { success, navMesh } = generateSoloNavMesh(
                new Float32Array(positions),
                new Int32Array(indices),
                config
            );

            if (!success || !navMesh) {
                console.error('[NavManager] Navmesh generation failed.');
                return;
            }

            // Tear down the old query before replacing it
            if (globalQuery) {
                try { globalQuery.destroy?.(); } catch (_) {}
            }

            globalNavMesh = navMesh;
            globalQuery   = new NavMeshQuery(globalNavMesh);

            console.log('[NavManager] Navmesh built successfully.');
        } catch (e) {
            console.error('[NavManager] rebuild() threw:', e);
        }
    }

    /* ── geometry extraction ────────────────────────────────────────────────── */

    /**
     * Walk every loaded chunk in VoxelTreadmill and emit a flat triangle soup of
     * the walkable surface.  We use the sampler's surface height for efficiency –
     * each (lx, lz) cell produces one quad (2 tris) at the correct world Y.
     *
     * We also check player mutations so mined/built voxels are reflected.
     *
     * @returns {{ positions: number[], indices: number[] }}
     */
    _extractSurfaceGeometry() {
        const t      = this._treadmill;
        const cs     = t.chunkSize;
        const radius = this.buildRadiusChunks;

        const positions = [];
        const indices   = [];
        let   vBase     = 0;

        // Determine player centre chunk
        const playerCX = t._playerCX ?? 0;
        const playerCZ = t._playerCZ ?? 0;

        for (let dcx = -radius; dcx <= radius; dcx++) {
            for (let dcz = -radius; dcz <= radius; dcz++) {
                const cx   = playerCX + dcx;
                const cz   = playerCZ + dcz;
                const id   = `${cx},${cz}`;

                // Only process chunks that are actually loaded in the scene
                if (!t._chunks.has(id)) continue;

                // Grab cached chunk data (compute if missing – cheap, heights only)
                let data = t._dataCache.get(id);
                if (!data) {
                    data = t._computeChunkData(cx, cz);
                    t._dataCache.set(id, data);
                }

                const originX = cx * cs;
                const originZ = cz * cs;

                for (let lx = 0; lx < cs; lx++) {
                    for (let lz = 0; lz < cs; lz++) {
                        // Resolve surface height: mutation map overrides base terrain
                        const surfY = this._resolvedSurfaceY(t, data, id, cx, cz, lx, lz, cs);

                        // Skip bedrock / very deep cells (no useful surface)
                        if (surfY <= t.floorLevel) continue;

                        // World-space corners of this 1×1 voxel column top face
                        const wx0 = originX + lx;
                        const wx1 = wx0 + 1;
                        const wz0 = originZ + lz;
                        const wz1 = wz0 + 1;
                        // Y sits at top of the surface voxel → surfY + 1 in Wonderland's coords
                        // (VoxelTreadmill solid test is wy <= surfH, so the walkable surface is surfY + 0.5 ish)
                        const wy  = surfY + 0.5;

                        // Emit 4 vertices (flat quad)
                        positions.push(wx0, wy, wz0);   // v+0
                        positions.push(wx1, wy, wz0);   // v+1
                        positions.push(wx1, wy, wz1);   // v+2
                        positions.push(wx0, wy, wz1);   // v+3

                        // Two triangles (CCW when viewed from above)
                        indices.push(vBase, vBase + 2, vBase + 1);
                        indices.push(vBase, vBase + 3, vBase + 2);
                        vBase += 4;
                    }
                }
            }
        }

        return { positions, indices };
    }

    /**
     * Return the effective surface Y for a cell, respecting player mutations.
     * Mirrors the lookup order in VoxelTreadmill._getBlockAt:
     *   1. VoxelModificationMap  (highest priority)
     *   2. Base WorldSampler height
     */
    _resolvedSurfaceY(t, data, chunkId, cx, cz, lx, lz, cs) {
        const baseHeight = data.heights[lx * cs + lz];
        const mods       = t._mutations.peekChunk(chunkId);
        if (!mods) return baseHeight;

        // Scan upward from base surface to find the highest solid voxel after mutations.
        // We clamp the search window to a reasonable range to avoid per-frame cost.
        let highest = baseHeight;

        for (let wy = baseHeight + 16; wy >= t.floorLevel; wy--) {
            const key = `${lx}_${wy}_${lz}`;
            const v   = mods[key];
            if (v === undefined) {
                // Not mutated – rely on base terrain height
                if (wy <= baseHeight) { highest = wy; break; }
                continue;
            }
            if (v !== 0) { highest = wy; break; }  // built block
            // v === 0 → mined; keep searching downward
        }

        return highest;
    }
}